import {
  AnimatedElement,
  AnimatedFormError,
  Box,
  ComboBox,
  ComboBoxContent,
  ComboBoxInput,
  ComboBoxItem,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogHeader,
  FormInput,
  FormInputField,
  Text,
} from "@prismicio/editor-ui"
import { useEffect, useMemo, useState } from "react"
import { z } from "zod"

import { useDefaultLocalesQuery } from "../../../settings/locales/useDefaultLocales"
import type { Locale } from "../../services/locales"
import { Flag } from "./Flag"
import type { SelectedLocale } from "./LocaleSelection"

interface SelectedCustomLocale extends SelectedLocale {
  customName: string
}

interface AddCustomLocaleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  addLocale: (customLocale: SelectedCustomLocale) => void
}

interface CustomLocaleFieldState {
  code: string
  name: string
  usesDefault: boolean
}

const initialFieldState: CustomLocaleFieldState = {
  code: "",
  name: "",
  usesDefault: false,
}

export function AddCustomLocaleDialog(props: AddCustomLocaleDialogProps) {
  const { open, onOpenChange, addLocale } = props

  const { data: defaultLocales } = useDefaultLocalesQuery()

  const {
    language,
    setLanguage,
    region,
    setRegion,
    customLocale,
    renderError,
    submitForm,
    resetForm,
  } = useAddCustomLocaleForm(addLocale)

  const { sortedLanguages, sortedRegions } = useMemo(
    () => extractLanguagesAndRegions(defaultLocales),
    [defaultLocales],
  )

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (!open) resetForm()
        onOpenChange(open)
      }}
      size="small"
      onOpenAutoFocus={(event) => event.preventDefault()}
      noDescription
    >
      <DialogHeader title="Create a custom locale" icon="add" />
      <DialogContent padding={16} gap={16}>
        <LocaleComboBox
          label="Language"
          description="This affects how content search will work (the grammar rules of the selected language will be applied)"
          error={renderError("language.name")}
          options={sortedLanguages}
          onChange={setLanguage}
          value={language}
        />

        <LocaleComboBox
          label="Region"
          description="This name will show up everywhere in the authors’ interface"
          error={undefined}
          options={sortedRegions}
          onChange={setRegion}
          value={region}
        />

        <LocaleCodeInput
          languageCode={language.code}
          languageCodeError={renderError("language.code")}
          onLanguageCodeChange={(value) =>
            setLanguage((current) => ({ ...current, code: value }))
          }
          languageCodeDisabled={language.usesDefault}
          regionCode={region.code}
          regionCodeError={renderError("region.code")}
          onRegionCodeChange={(value) =>
            setRegion((current) => ({ ...current, code: value }))
          }
          regionCodeDisabled={region.usesDefault}
        />

        {customLocale && <CustomLocalePreview customLocale={customLocale} />}
      </DialogContent>
      <DialogActions>
        <DialogCancelButton />
        <DialogActionButton onClick={submitForm}>Add locale</DialogActionButton>
      </DialogActions>
    </Dialog>
  )
}

const CustomLocaleFormSchema = z.object({
  language: z.object({
    name: z.string().min(1, { message: "Please enter the language name." }),
    code: z.string().length(2, {
      message: "Language code must be exactly 2 characters long.",
    }),
  }),
  region: z.object({
    name: z.string().optional(),
    code: z
      .string()
      .optional()
      .refine((val) => !val || val.length === 2, {
        message: "Region code must be exactly 2 characters long.",
      }),
  }),
})

type PathToErrorMessageMap = Record<string, string>

function useAddCustomLocaleForm(
  addLocale: AddCustomLocaleDialogProps["addLocale"],
) {
  const [language, setLanguage] =
    useState<CustomLocaleFieldState>(initialFieldState)
  const [region, setRegion] =
    useState<CustomLocaleFieldState>(initialFieldState)

  const [errors, setErrors] = useState<PathToErrorMessageMap | undefined>()
  const [showErrors, setShowErrors] = useState<boolean>(false)

  function resetForm() {
    setLanguage(initialFieldState)
    setRegion(initialFieldState)
    setErrors(undefined)
    setShowErrors(false)
  }

  const isLanguageFilled = Boolean(language.name && language.code)

  const customLocale: SelectedCustomLocale | undefined = useMemo(() => {
    if (!isLanguageFilled) return undefined

    const localeId = region.code
      ? `${language.code}-${region.code}`
      : language.code

    const localeLabel = region.name
      ? `${language.name} - ${region.name}`
      : language.name

    return {
      id: localeId,
      customName: localeLabel,
    }
  }, [isLanguageFilled, language.code, language.name, region.code, region.name])

  // Parse errors on change
  useEffect(() => {
    const parseResult = CustomLocaleFormSchema.safeParse({ language, region })
    if (parseResult.success) {
      setErrors(undefined)
      setShowErrors(false)
      return
    }

    const errorObject = parseResult.error.issues.reduce<PathToErrorMessageMap>(
      (acc, item) => {
        const pathKey = item.path.join(".")
        acc[pathKey] = item.message
        return acc
      },
      {},
    )

    setErrors(errorObject)
  }, [language, region])

  function submitForm(): void {
    if (errors || !customLocale) {
      setShowErrors(true)
      return
    }

    addLocale(customLocale)
    resetForm()
  }

  function renderError(jsonPath: string): string | undefined {
    if (!showErrors) return undefined
    return errors?.[jsonPath]
  }

  return {
    language,
    setLanguage,
    region,
    setRegion,
    customLocale,
    renderError,
    resetForm,
    submitForm,
  }
}

interface CustomLocalePreviewProps {
  customLocale: SelectedCustomLocale
}

function CustomLocalePreview(props: CustomLocalePreviewProps) {
  const { customLocale } = props

  return (
    <AnimatedElement>
      <Box flexDirection="column">
        <Text variant="bold">
          How authors will see the locale while editing content
        </Text>

        <Box
          border
          borderStyle="solid"
          borderRadius={8}
          padding={{ inline: 12, block: 8 }}
          gap={8}
          alignItems="center"
        >
          <Flag locale={customLocale.id} />
          <Text variant="normal">{customLocale.customName}</Text>
        </Box>
      </Box>
    </AnimatedElement>
  )
}

interface LocaleComboBoxProps {
  label: string
  description: string
  error: string | undefined
  options: (Language | Region)[]
  value: CustomLocaleFieldState
  onChange: React.Dispatch<React.SetStateAction<CustomLocaleFieldState>>
}

function LocaleComboBox(props: LocaleComboBoxProps) {
  const { label, description, error, options, onChange, value } = props

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(value.name.toLowerCase()),
  )

  function handleInputChange(value: string) {
    onChange((current) => {
      // If user start typing the language / region name but before a default one was selected
      // we want to reset the state and set the input value as the new name
      if (current.usesDefault) {
        return {
          name: value,
          code: "",
          usesDefault: false,
        }
      }

      return { ...current, name: value }
    })
  }

  return (
    <FormInputField
      label={label}
      description={description}
      descriptionPosition="bottom"
      error={error}
    >
      <ComboBox variant="detached" forceNoFormInput>
        <ComboBoxInput
          value={value.name}
          onValueChange={handleInputChange}
          startAdornment={false}
        />
        {filteredOptions.length > 0 && (
          <ComboBoxContent paddingBlock={2}>
            {filteredOptions.map((option) => (
              <ComboBoxItem
                key={option.id}
                value={option.id}
                checked={option.id === value.code}
                onCheckedChange={(checked) => {
                  if (checked) {
                    onChange({
                      name: option.label,
                      code: option.id,
                      usesDefault: true,
                    })
                  }
                }}
                iconStyle={{ color: "grey11", size: "small" }}
              >
                <Box padding={{ inline: 8, block: 4 }}>
                  <Text component="span">{option.label}</Text>
                </Box>
              </ComboBoxItem>
            ))}
          </ComboBoxContent>
        )}
      </ComboBox>
    </FormInputField>
  )
}

interface LocaleCodeInputProps {
  languageCode: string
  languageCodeDisabled: boolean
  languageCodeError: string | undefined
  regionCode: string
  regionCodeDisabled: boolean
  regionCodeError: string | undefined
  onLanguageCodeChange: (value: string) => void
  onRegionCodeChange: (value: string) => void
}

function LocaleCodeInput(props: LocaleCodeInputProps) {
  const {
    languageCode,
    languageCodeDisabled,
    languageCodeError,
    regionCode,
    regionCodeDisabled,
    regionCodeError,
    onLanguageCodeChange,
    onRegionCodeChange,
  } = props

  return (
    <Box flexDirection="column">
      <Text variant="bold">Locale code</Text>
      <Box alignItems="center">
        <FormInput
          value={languageCode}
          error={Boolean(languageCodeError)}
          disabled={languageCodeDisabled}
          onValueChange={onLanguageCodeChange}
          maxLength={2}
          sx={{ width: 42 }}
        />
        <Text variant="bold" sx={{ marginInline: 8 }}>
          -
        </Text>
        <FormInput
          value={regionCode}
          error={Boolean(regionCodeError)}
          disabled={regionCodeDisabled}
          onValueChange={onRegionCodeChange}
          maxLength={2}
          sx={{ width: 42 }}
        />
      </Box>
      <AnimatedFormError error={languageCodeError} />
      <AnimatedFormError error={regionCodeError} />
      <Text color="grey11" sx={{ marginTop: 4 }}>
        It will appear in the API requests and responses
      </Text>
    </Box>
  )
}

function alphabeticSortByLabel(a: Locale, b: Locale) {
  return a.label.localeCompare(b.label)
}

interface Language extends Locale {}
interface Region extends Locale {}

interface ExtractLanguagesAndRegionsReturn {
  sortedLanguages: Language[]
  sortedRegions: Region[]
}

function extractLanguagesAndRegions(
  defaultLocales: Locale[],
): ExtractLanguagesAndRegionsReturn {
  const { languagesMap, regionsMap } = defaultLocales.reduce<{
    languagesMap: Map<string, Language>
    regionsMap: Map<string, Region>
  }>(
    (result, locale) => {
      const [languageCode, regionCode] = locale.id.split("-")
      const [languageName, regionName] = locale.label.split(" - ")

      if (languageCode && languageName) {
        result.languagesMap.set(languageCode, {
          id: languageCode,
          label: languageName,
        })
      }

      if (regionCode && regionName) {
        result.regionsMap.set(regionCode, {
          id: regionCode,
          label: regionName,
        })
      }

      return result
    },
    { languagesMap: new Map(), regionsMap: new Map() },
  )

  const sortedLanguages = Array.from(languagesMap.values()).sort(
    alphabeticSortByLabel,
  )
  const sortedRegions = Array.from(regionsMap.values()).sort(
    alphabeticSortByLabel,
  )

  return {
    sortedLanguages,
    sortedRegions,
  }
}
